import React, { PropsWithChildren, useEffect, useState } from "react";

import { Divider } from "../../../../../../components/Divider";
import { Grid, GridCol } from "../../../../../../components/Grid";
import Toggle from "../../../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import Spacer from "../../../../../../components/Spacer";
import Text from "../../../../../../components/Text";

import { useCompanyFunction, useTeamsHasAdminConsent } from "../../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

import css from "./NotificationsChannels.module.scss";

enum NotificationChannelTypes {
	Email = "email",
	MicrosoftTeams = "ms-teams",
}

type notificationType = "asap-daily" | "digest" | "admin-digest";

interface NotificationChannelsProps {
	eligibleChannels: NotificationChannelTypes[];
	enabledNotificationChannels: NotificationChannelTypes[];
	onChangeEnabledNotificationChannels: (type: notificationType, enabledChannels: NotificationChannelTypes[]) => void;
	notificationType: notificationType;
	isLoading: boolean;
}

const notificationChannelLabels: Record<NotificationChannelTypes, string> = {
	[NotificationChannelTypes.Email]: "Email",
	[NotificationChannelTypes.MicrosoftTeams]: "Microsoft Teams",
};

/**
 * Renders a card component with children with optional padding and a height
 * `height` is the height of the card
 * `padY` is the padding on the top and bottom of the card
 * `padX` is the padding on the left and right of the card
 *
 */
export const NotificationChannels = (props: PropsWithChildren<NotificationChannelsProps>) => {
	const {
		eligibleChannels,
		enabledNotificationChannels,
		onChangeEnabledNotificationChannels,
		notificationType,
		isLoading,
	} = props;

	// we use  single requst to fetch this data. But we display multiple toggles.
	//. The optimisticToggleLoading var is used to make the loading state of
	// the toggles independent.
	const [optimisticToggleLoading, setOptimisticToggleLoading] = useState({});
	const { companyFunction: teamsUserNotificationsFeatureEnabled } = useCompanyFunction("teams-user-notifications");
	const { data: adminConsentResponse } = useTeamsHasAdminConsent();
	const { value: teamsUserNotificationsFF } = useFeatureFlagQuery("ff-is-teams-user-notifications-enabled", false);
	const hideTeamsNotificationsToggle =
		!teamsUserNotificationsFF ||
		teamsUserNotificationsFeatureEnabled !== "enabled" ||
		adminConsentResponse?.hasAdminConsentV2 !== true ||
		!eligibleChannels.includes(NotificationChannelTypes.MicrosoftTeams);

	useEffect(() => {
		// when isLoading switches from true to false, we
		// reset the optimisticToggleLoading state
		if (!isLoading) {
			setOptimisticToggleLoading(
				// set all to false
				Object.keys(optimisticToggleLoading).reduce((acc, key) => {
					acc[key] = false;
					return acc;
				}, {})
			);
		}
	}, [isLoading]);

	const onToggleChange = (channel: NotificationChannelTypes, enabled: boolean) => {
		//
		setOptimisticToggleLoading({
			...optimisticToggleLoading,
			[channel]: true,
		});

		onChangeEnabledNotificationChannels(
			notificationType,
			enabled
				? [...enabledNotificationChannels, channel]
				: enabledNotificationChannels.filter((c) => c !== channel)
		);
	};

	const selectChannelElement = (channel: NotificationChannelTypes) => {
		return (
			<div key={channel}>
				<Grid cols={5}>
					<GridCol span={3}>
						<Text size="paragraph-2" weight="regular" color="neutral-9">
							{notificationChannelLabels[channel]}
						</Text>
					</GridCol>
					<GridCol span={1}>
						<div>
							<div style={{ width: 40 }}>
								<Toggle
									onChange={(newValue) => {
										onToggleChange(channel, newValue);
									}}
									value={enabledNotificationChannels.includes(channel)}
									isLoading={optimisticToggleLoading[channel]}
								/>
							</div>
						</div>
					</GridCol>
				</Grid>
				<Spacer height={1} />
			</div>
		);
	};

	return (
		<div>
			<GridCol className={css.notificationsChannels} span={5}>
				<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
					Where would you like to receive notifications?
				</Text>
				<Spacer height={1} />
				{selectChannelElement(NotificationChannelTypes.Email)}
				{hideTeamsNotificationsToggle ? null : selectChannelElement(NotificationChannelTypes.MicrosoftTeams)}
			</GridCol>
			<Divider />
		</div>
	);
};
